import { ActionIcon, Tooltip, useMantineTheme } from "@mantine/core";
import { FilePdfIcon } from "@phosphor-icons/react";
import jsPDF from "jspdf";
import autoTable, { UserOptions } from "jspdf-autotable";
import { ComponentProps } from "react";

import { ACTION_ICON_PROPS, ICON_PROPS } from "./ExportBtn.constants";

type ExportColumn<T> =
  | {
      key: keyof T;
      headerName: string;
      getValue?: (row: T) => string | number;
    }
  | {
      key: "CUSTOM";
      headerName: string;
      getValue: (row: T) => string | number;
    };

type ExportBtnPDFProps<T> = {
  data: T[];
  columnsV2: ExportColumn<T>[];
  title: string;
  actionIconProps?: ComponentProps<typeof ActionIcon>;
  tooltipProps?: ComponentProps<typeof Tooltip>;
};

export function ExportBtnPDF<T>({ data, columnsV2, title, actionIconProps, tooltipProps }: ExportBtnPDFProps<T>) {
  const { colors } = useMantineTheme();
  const primaryColor = colors.primary[5];
  const resolvedColumns = columnsV2.filter((column) => Boolean(column.headerName));

  const buildHead = () => [resolvedColumns.map((column) => column.headerName)];

  const buildBody = () =>
    data.map((row) =>
      resolvedColumns.map((column) => {
        let value: unknown;

        if (column.key === "CUSTOM") {
          value = column.getValue?.(row);
        } else if (column.getValue) {
          value = column.getValue(row);
        } else {
          value = row[column.key];
        }

        return value == null ? "" : String(value);
      })
    );

  const exportPDF = () => {
    const doc = new jsPDF({
      unit: "pt",
      format: "a4",
      orientation: "portrait"
    });

    const head = buildHead();
    const body = buildBody();

    doc.setFontSize(15);
    doc.text(title, 40, 40);

    const options: UserOptions = {
      startY: 50,
      head,
      body,
      margin: { left: 40, right: 40, top: 50, bottom: 30 },
      headStyles: {
        fillColor: primaryColor,
        textColor: 255,
        fontStyle: "bold",
        halign: "left"
      },
      styles: {
        fontSize: 10,
        textColor: [80, 80, 80],
        cellPadding: 5,
        lineColor: [220, 220, 220],
        lineWidth: 0.2
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      }
    };

    autoTable(doc, options);
    doc.save(`${title}.pdf`);
  };

  return (
    <Tooltip label="Exportar PDF" color="red" {...tooltipProps}>
      <ActionIcon {...ACTION_ICON_PROPS} {...actionIconProps} color="red" onClick={exportPDF}>
        <FilePdfIcon {...ICON_PROPS} />
      </ActionIcon>
    </Tooltip>
  );
}
